import { useState } from 'react';
import { Link } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, BookOpen, Settings, GraduationCap, Download, Loader2,
  Target, Phone, Users, BookOpenCheck, Package, ClipboardCheck, MessageCircle, Gift
} from 'lucide-react';
import TrainingGuidePDF from '@/pdf/TrainingGuidePDF';

const AdminSidebar = () => {
  return (
    <aside className="fixed left-0 top-0 h-screen w-64 admin-sidebar border-r border-[#2A2A2A] pt-20 hidden lg:block">
      <nav className="p-4 space-y-2">
        <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <LayoutDashboard size={20} />
          <span>Dashboard</span>
        </Link>
        <Link to="/admin/courses" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <BookOpen size={20} />
          <span>Course Guide</span>
        </Link>
        <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <Settings size={20} />
          <span>Class Settings</span>
        </Link>
        <Link to="/admin/perks" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <Gift size={20} />
          <span>Perks & Tiers</span>
        </Link>
        <Link to="/admin/training" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37]">
          <GraduationCap size={20} />
          <span>Staff Training</span>
        </Link>
      </nav>
      <div className="absolute bottom-4 left-4 right-4">
        <div className="p-4 rounded-lg border border-[#2A2A2A] bg-black/30">
          <p className="text-xs text-[#A1A1AA] mb-2">Public Forms</p>
          <Link to="/apply" target="_blank" className="text-[#D4AF37] text-sm hover:underline block mb-1">
            &rarr; New Students
          </Link>
          <Link to="/repackage" target="_blank" className="text-[#D4AF37] text-sm hover:underline block mb-1">
            &rarr; Existing Students
          </Link>
          <Link to="/returning" target="_blank" className="text-[#D4AF37] text-sm hover:underline block">
            &rarr; Returning Students
          </Link>
        </div>
      </div>
    </aside>
  );
};

const sections = [
  {
    icon: Target,
    number: '1',
    title: 'The NSM Mission & Your Role',
    description: 'What Nathaniel School of Music is, who our students are, and your purpose as a communications staff member.',
    highlights: ['School mission & values', 'Student demographics', 'Your core responsibilities']
  },
  {
    icon: Users,
    number: '2',
    title: 'Student Types',
    description: 'Understanding New, Existing, and Returning students - what each form captures and how they differ.',
    highlights: ['New Student applications', 'Repackage requests', 'Returning student process']
  },
  {
    icon: BookOpenCheck,
    number: '3',
    title: 'Admissions Status Workflow',
    description: 'The complete pipeline from Pending through to Enrolled or Declined, with actions for each stage.',
    highlights: ['6-stage pipeline', 'Actions per stage', 'Timeline expectations']
  },
  {
    icon: Phone,
    number: '4',
    title: 'The Phone Call Masterclass',
    description: 'Preparation checklists, opening scripts for each student type, discovery questions, goal mapping, and closing techniques.',
    highlights: ['Pre-call preparation', 'Opening scripts', 'Discovery questions', 'Closing techniques']
  },
  {
    icon: BookOpen,
    number: '5',
    title: 'The 7 Classrooms',
    description: 'Deep dive into every classroom with taglines, overviews, target audiences, benefits, talking points, and all classes with descriptions.',
    highlights: ['Music Gym', 'Music Factory', 'Modular Piano', 'Theory & Ear Training', 'Seasonal & Genre', 'New Offerings', 'Foundation Lessons']
  },
  {
    icon: Package,
    number: '6',
    title: 'Building a Custom Package',
    description: 'How to use the Package Builder, match hours to goals, and allocate classes effectively.',
    highlights: ['Step-by-step process', 'Hour allocation guidelines', 'Example packages']
  },
  {
    icon: ClipboardCheck,
    number: '7',
    title: 'Post-Call Checklist & Follow-Up',
    description: 'What to update after every call, when to send packages, and the follow-up cadence.',
    highlights: ['Post-call tasks', 'Package sending process', 'Follow-up schedule']
  },
  {
    icon: MessageCircle,
    number: '8',
    title: 'Common Objections & Responses',
    description: 'Ready-made responses for price concerns, time constraints, indecision, and more.',
    highlights: ['Price objections', 'Time concerns', '"I\'ll think about it"', 'Past failures']
  }
];

export const AdminTrainingGuide = () => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await pdf(<TrainingGuidePDF />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'NSM-Staff-Training-Guide.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header variant="solid" />
      <AdminSidebar />

      <main className="lg:ml-64 pt-20 p-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
            <div>
              <h1 className="font-heading text-3xl text-[#EDEDED] mb-2">Staff Training Guide</h1>
              <p className="text-[#A1A1AA]">
                Comprehensive admissions and communications training for NSM staff
              </p>
            </div>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="btn-gold flex items-center gap-2 px-6 py-3 text-base shrink-0"
            >
              {downloading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download size={20} />
                  Download PDF
                </>
              )}
            </Button>
          </div>

          <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                <GraduationCap className="text-[#D4AF37]" size={24} />
              </div>
              <div>
                <h2 className="font-heading text-lg text-[#F5F5DC] mb-1">About This Guide</h2>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">
                  This training guide covers everything you need to know as an admin or communications staff member at Nathaniel School of Music. From understanding our mission and student types, to mastering phone calls and building custom packages, this document is your comprehensive reference. Download the PDF to keep it handy during calls.
                </p>
              </div>
            </div>
          </div>

          <h2 className="font-heading text-xl text-[#F5F5DC] mb-4">Guide Contents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.number}
                  className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-5 hover:border-[#D4AF37]/30 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                      <Icon className="text-[#D4AF37]" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[#D4AF37] text-xs font-mono">Section {section.number}</span>
                      </div>
                      <h3 className="font-heading text-base text-[#F5F5DC] mb-1">{section.title}</h3>
                      <p className="text-[#A1A1AA] text-sm mb-3 leading-relaxed">{section.description}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {section.highlights.map((h, i) => (
                          <span
                            key={i}
                            className="text-xs px-2 py-0.5 rounded bg-[#D4AF37]/5 text-[#D4AF37]/80 border border-[#D4AF37]/10"
                          >
                            {h}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 bg-[#121212] border border-[#2A2A2A] rounded-xl p-6 text-center">
            <p className="text-[#A1A1AA] text-sm mb-4">
              Ready to get started? Download the complete guide as a beautifully formatted PDF.
            </p>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="btn-gold flex items-center gap-2 px-8 py-3 text-base mx-auto"
            >
              {downloading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download size={20} />
                  Download Staff Training Guide
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminTrainingGuide;
